import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';

import { ICanvasSketch } from '../global/interfaces';
import { SHAPES } from '../global/constants/general';
import { useSessionContext } from './SessionContext';
import useCanvasSketch from '../hooks/useCanvasSketch';

const defaultState: ICanvasSketch = {
  canvasSketchRef: null,
  setSendPayload: () => {},

  color: '',
  setColor: () => {},

  penSize: 1,
  setPenSize: () => {},

  activeTab: '',
  setActiveTab: () => {},

  activeShape: '',
  setActiveShape: () => {},

  strokeList: [],
  setStrokeList: () => [],

  isStrokeListUpdated: false,
  setIsStrokeListUpdated: (_: boolean) => {},

  isPenVisible: false,
  setIsPenVisible: () => {},

  isLayersVisible: false,
  setIsLayersVisible: () => {},

  croppedUndoImages: [],
  setCroppedUndoImages: () => {},

  handleClickOutside: () => {},

  isColorPaletteOpen: false,
  setIsColorPaletteOpen: () => {},

  handleToggleShapesModal: () => {},

  isShapesVisible: false,
  setIsShapeVisible: () => {},

  handleToggleColorPalette: () => {},

  shapesRef: { current: null },
  paletteRef: { current: null },

  handleSlider: () => {},
  handleSetActiveShape: () => {},
  handleSelectColor: () => {},

  handleSaveImage: () => {},

  //isCloseEditorModalOpen: false,
  //setIsCloseEditorModalOpen: () => {},

  mouseDown: () => {},
  mouseMove: () => {},
  mouseUp: () => {},
  mouseOut: () => {},

  draw: () => {},
  undoChanges: () => {},
  clearCanvas: () => {},
};

const CanvasSketchContext = createContext(defaultState);

export function CanvasSketchProvider({ children }: { children: JSX.Element }) {
  const {
    pen: penSize,
    setPen: setPenSize,
    shape,
    setShape,

    canvasSketchRef,
    setSendPayload,
    color,
    setColor,
    strokeList,
    setStrokeList,
    isStrokeListUpdated,
    setIsStrokeListUpdated,
    mouseDown,
    mouseMove,
    mouseUp,
    mouseOut,
    draw,
    undoChanges,
    clearCanvas,
  } = useCanvasSketch();

  const [activeTab, setActiveTab] = useState('DRAW');
  const [activeShape, setActiveShape] = useState('SQUARE');

  const [isPenVisible, setIsPenVisible] = useState<boolean>(false);
  const [isLayersVisible, setIsLayersVisible] = useState<boolean>(false);
  const [isShapesVisible, setIsShapeVisible] = useState<boolean>(false);

  const [croppedUndoImages, setCroppedUndoImages] = useState<any>([]);
  const [isColorPaletteOpen, setIsColorPaletteOpen] = useState<boolean>(false);

  const shapesRef = useRef<any>(null);
  const paletteRef = useRef<any>(null);

  const {
    booking,
    imagesMapRef,
    strokesLayer,
    selectedImage,
    setSelectedImage,
  } = useSessionContext();

  const handleClickOutside = () => {
    setIsPenVisible(false);
    setIsColorPaletteOpen(false);
    setIsShapeVisible(false);
    setIsLayersVisible(false);
  };

  useEffect(() => {
    setShape(SHAPES[activeTab === 'Shape' ? activeShape : activeTab]);
  }, [activeTab, activeShape]);

  const handleToggleShapesModal = useCallback(() => {
    setIsShapeVisible(!isShapesVisible);
    setActiveTab('Shape');
    console.log(isShapesVisible);
  }, [isShapesVisible]);

  const handleToggleColorPalette = useCallback(
    () => setIsColorPaletteOpen(!isColorPaletteOpen),
    [isColorPaletteOpen],
  );

  const handleSelectColor = (color: { hex: string }) => {
    setColor(color.hex);
  };

  const handleSlider = (event: Event, value: number | number[]) => {
    setPenSize(value as number);
  };

  const handleSetActiveShape = (shape: string) => {
    setActiveShape(shape);
    handleToggleShapesModal();
  };

  const handleSaveImage = async () => {
    if (!booking) return;

    let layer = strokesLayer;
    const image = selectedImage?.id
      ? imagesMapRef?.current?.get(selectedImage.id)
      : undefined;
    if (image) {
      if (!image.strokes) {
        layer = 1;
        image.strokes = [[], strokeList];
      } else {
        const strokes = image.strokes.reduce(
          (acc: any, current: any, index: number) => {
            if (
              (index === layer && strokeList.length > 0) ||
              (index !== layer && current.length > 0)
            ) {
              acc.push(index === layer ? strokeList : current);
            }
            return acc;
          },
          [[]],
        );

        if (layer === 0) ++layer;
        if (layer >= strokes.length) layer = 0;

        image.layer = layer;
        image.strokes = strokes;
      }
    }

    setSelectedImage(null);
  };

  return (
    <CanvasSketchContext.Provider
      value={{
        canvasSketchRef,
        setSendPayload,

        color,
        setColor,

        penSize,
        setPenSize,

        activeTab,
        setActiveTab,

        activeShape,
        setActiveShape,

        strokeList,
        setStrokeList,

        isStrokeListUpdated,
        setIsStrokeListUpdated,

        isPenVisible,
        setIsPenVisible,

        isLayersVisible,
        setIsLayersVisible,

        croppedUndoImages,
        setCroppedUndoImages,

        handleClickOutside,

        isColorPaletteOpen,
        setIsColorPaletteOpen,

        handleToggleShapesModal,

        isShapesVisible,
        setIsShapeVisible,

        handleToggleColorPalette,

        shapesRef,
        paletteRef,

        handleSlider,

        handleSetActiveShape,

        handleSelectColor,

        handleSaveImage,

        //isCloseEditorModalOpen,
        //setIsCloseEditorModalOpen,

        mouseDown,
        mouseMove,
        mouseUp,
        mouseOut,

        draw,
        undoChanges,
        clearCanvas,
      }}>
      {children}
    </CanvasSketchContext.Provider>
  );
}

export function useCanvasSketchContext() {
  return useContext(CanvasSketchContext);
}
